'use client';

import { useContext, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import axios from 'axios';
import AuthContext from '@/providers/auth-context';
import useAnalyticsTracking from '@/components/common/analytics/use_analytics_tracking';
import classes from './survey_drawer_manager.module.css';

const SEEN_KEY = 'survey_drawer_seen_v1';
const WEEKLY_KEY_PREFIX = 'survey_drawer_weekly_v1';
const ENABLED = process.env.NEXT_PUBLIC_SURVEY_MVP_ENABLED === 'enabled';
const FIRST_SCROLL_PX = 12;
const IS_NON_PROD = process.env.NODE_ENV !== 'production';

const DEFAULT_SURVEY = {
    id: 'mvp-default',
    key: 'mvp-default',
    title: 'Gyors kérdőív',
    questions: [
        {
            id: 'q1',
            title: 'Mennyire hasznos neked ez a funkció?',
            options: [
                { value: '5', label: 'Nagyon hasznos' },
                { value: '4', label: 'Hasznos' },
                { value: '3', label: 'Közepes' },
                { value: '2', label: 'Kevésbé hasznos' },
                { value: '1', label: 'Nem hasznos' }
            ],
            negativeValues: ['1', '2']
        }
    ],
    triggerDelaySec: 20,
    firstScrollEnabled: true,
    scrollRatioEnabled: true,
    scrollRatio: 0.75,
    weeklyCooldownDays: 7,
    showOnDesktop: true,
    showOnMobile: true,
    followupMode: 'always',
    questionOrderMode: 'sequential'
};

function shuffleArray(input = []) {
    const a = [...input];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function getWeeklyCooldownMs(days) {
    const n = Number(days);
    const safeDays = Number.isFinite(n) && n > 0 ? n : 7;
    return safeDays * 24 * 60 * 60 * 1000;
}

function isSeenThisSession() {
    try {
        return sessionStorage.getItem(SEEN_KEY) === '1';
    } catch (_) {
        return false;
    }
}

function markSeenThisSession() {
    try {
        sessionStorage.setItem(SEEN_KEY, '1');
    } catch (_) {
        // best-effort
    }
}

function getWeeklyIdentity(userId) {
    return userId && Number(userId) > 0 ? `user:${Number(userId)}` : 'nouser';
}

function getWeeklyKey(userId) {
    return `${WEEKLY_KEY_PREFIX}:${getWeeklyIdentity(userId)}`;
}

function isBlockedByWeeklyCooldown(userId, weeklyCooldownDays = 7) {
    try {
        const raw = localStorage.getItem(getWeeklyKey(userId));
        if (!raw) return false;

        const ts = Number(raw);
        if (!Number.isFinite(ts) || ts <= 0) return false;

        return (Date.now() - ts) < getWeeklyCooldownMs(weeklyCooldownDays);
    } catch (_) {
        return false;
    }
}

function markSeenForWeek(userId) {
    try {
        localStorage.setItem(getWeeklyKey(userId), String(Date.now()));
    } catch (_) {
        // best-effort
    }
}

function clearAllSurveyLocks() {
    try {
        sessionStorage.removeItem(SEEN_KEY);
    } catch (_) {
        // best-effort
    }

    try {
        const keysToRemove = Object.keys(localStorage).filter((key) => key.startsWith(WEEKLY_KEY_PREFIX));
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (_) {
        // best-effort
    }
}

function getDebugFlags() {
    if (typeof window === 'undefined' || !IS_NON_PROD) {
        return {
            forceOpen: false,
            resetLocks: false
        };
    }

    const params = new URLSearchParams(window.location.search || '');
    return {
        forceOpen: params.get('survey_force') === '1',
        resetLocks: params.get('survey_reset') === '1'
    };
}

function getScrollProgress(target = null) {
    const doc = document.documentElement;
    const body = document.body;
    const scrollRoot = target && target instanceof HTMLElement
        ? target
        : document.querySelector('[data-survey-scroll-root="1"]');

    const viewport = scrollRoot
        ? scrollRoot.clientHeight
        : (window.innerHeight || doc.clientHeight || 0);
    const total = scrollRoot
        ? Math.max(scrollRoot.scrollHeight, scrollRoot.offsetHeight)
        : Math.max(
            body.scrollHeight,
            doc.scrollHeight,
            body.offsetHeight,
            doc.offsetHeight
        );
    const scrollTop = scrollRoot ? scrollRoot.scrollTop : window.scrollY;

    if (total <= 0) return 0;
    const scrollable = total - viewport;
    if (scrollable <= 0) return 1;

    return Math.max(0, Math.min(1, scrollTop / scrollable));
}

function normalizeSurvey(rawSurvey) {
    if (!rawSurvey || typeof rawSurvey !== 'object') return null;

    const rawQuestions = Array.isArray(rawSurvey.questions) ? rawSurvey.questions : [];
    const questions = rawQuestions
        .map((q, index) => {
            const options = Array.isArray(q?.options)
                ? q.options
                    .map((opt) => {
                        const value = String(opt?.value || '').trim();
                        const label = String(opt?.label || '').trim();
                        return value && label ? { value, label } : null;
                    })
                    .filter(Boolean)
                : [];

            if (options.length < 2) return null;

            const id = String(q?.id || `q${index + 1}`).trim();
            const title = String(q?.title || '').trim();
            if (!title) return null;

            const negativeValues = Array.isArray(q?.negativeValues)
                ? q.negativeValues.map((v) => String(v || '').trim()).filter(Boolean)
                : [];

            return {
                id,
                title,
                options,
                negativeValues
            };
        })
        .filter(Boolean);

    if (questions.length === 0) return null;

    const scrollRatioRaw = Number(rawSurvey.scrollRatio);
    const scrollRatio = Number.isFinite(scrollRatioRaw)
        ? Math.max(0.05, Math.min(1, scrollRatioRaw))
        : DEFAULT_SURVEY.scrollRatio;

    return {
        id: String(rawSurvey.id || DEFAULT_SURVEY.id),
        key: String(rawSurvey.key || rawSurvey.id || DEFAULT_SURVEY.key),
        title: String(rawSurvey.title || DEFAULT_SURVEY.title),
        questions,
        audienceMode: ['both', 'logged_in', 'guest'].includes(rawSurvey.audienceMode)
            ? rawSurvey.audienceMode
            : 'both',
        triggerDelaySec: Math.max(1, Number(rawSurvey.triggerDelaySec) || DEFAULT_SURVEY.triggerDelaySec),
        firstScrollEnabled: rawSurvey.firstScrollEnabled !== false,
        scrollRatioEnabled: rawSurvey.scrollRatioEnabled !== false,
        scrollRatio,
        weeklyCooldownDays: Math.max(1, Number(rawSurvey.weeklyCooldownDays) || DEFAULT_SURVEY.weeklyCooldownDays),
        showOnDesktop: rawSurvey.showOnDesktop !== false,
        showOnMobile: rawSurvey.showOnMobile !== false,
        followupMode: rawSurvey.followupMode === 'opt_in' ? 'opt_in' : 'always',
        questionOrderMode: rawSurvey.questionOrderMode === 'random' ? 'random' : 'sequential'
    };
}

export default function SurveyDrawerManager() {
    const pathname = usePathname();
    const { userData } = useContext(AuthContext);
    const { trackEvent } = useAnalyticsTracking('surveyTracking');

    const [survey, setSurvey] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isAutoTriggerSuppressed, setIsAutoTriggerSuppressed] = useState(false);
    const [questionOrder, setQuestionOrder] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [suggestions, setSuggestions] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isThankYouVisible, setIsThankYouVisible] = useState(false);

    const debugFlags = useMemo(() => getDebugFlags(), [pathname]);
    const isMobile = useMemo(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia('(max-width: 902px)').matches;
    }, [pathname]);

    const getCurrentScrollTop = (target = null) => {
        if (typeof window === 'undefined') return 0;
        const scrollRoot = target && target instanceof HTMLElement
            ? target
            : document.querySelector('[data-survey-scroll-root="1"]');
        return scrollRoot ? scrollRoot.scrollTop : window.scrollY;
    };

    useEffect(() => {
        if (debugFlags.resetLocks) {
            clearAllSurveyLocks();
            setIsAutoTriggerSuppressed(false);
        }
    }, [debugFlags.resetLocks]);

    useEffect(() => {
        // Reload should not keep the minimized suppression state.
        setIsAutoTriggerSuppressed(false);
    }, [pathname]);

    useEffect(() => {
        if (!survey) return;

        // While minimized/opened manually we keep auto triggers disabled.
        if (isAutoTriggerSuppressed || isOpen || isMinimized) {
            return;
        }

        const blocked = isSeenThisSession() || isBlockedByWeeklyCooldown(userData?.id, survey.weeklyCooldownDays);
        const platformBlocked = (isMobile && !survey.showOnMobile) || (!isMobile && !survey.showOnDesktop);

        if (!ENABLED || platformBlocked || (!debugFlags.forceOpen && blocked)) {
            return;
        }

        let disposed = false;
        let displayed = false;
        let timerId = null;
        let lastScrollY = getCurrentScrollTop();
        const triggerDelayMs = Math.max(1, Number(survey.triggerDelaySec || 20)) * 1000;
        const scrollRatio = Number(survey.scrollRatio || 0.75);

        const showDrawer = (source = 'in_page_trigger') => {
            const runtimeBlocked = isSeenThisSession() || isBlockedByWeeklyCooldown(userData?.id, survey.weeklyCooldownDays);
            if (disposed || displayed || (!debugFlags.forceOpen && runtimeBlocked)) return;
            displayed = true;
            setIsOpen(true);
            setIsMinimized(false);
            const baseOrder = survey.questions.map((q) => q.id);
            const runtimeOrder = survey.questionOrderMode === 'random' ? shuffleArray(baseOrder) : baseOrder;
            setQuestionOrder(runtimeOrder);
            setCurrentQuestionIndex(0);
            setAnswers({});
            setSuggestions({});
            setIsThankYouVisible(false);
            trackEvent('survey_shown', {
                source,
                path: pathname || '',
                user_id: userData?.id || 0,
                survey_id: survey.id
            });
        };

        const onScroll = (event) => {
            const eventTarget = event?.target instanceof HTMLElement ? event.target : null;
            const currentScrollY = getCurrentScrollTop(eventTarget);
            const isDownScroll = currentScrollY > lastScrollY;
            const didReachScrollRatio = survey.scrollRatioEnabled && getScrollProgress(eventTarget) >= scrollRatio;
            // If ratio trigger is active, it should be authoritative to avoid early open.
            const didFirstMeaningfulScroll = !survey.scrollRatioEnabled && survey.firstScrollEnabled && isDownScroll && currentScrollY >= FIRST_SCROLL_PX;
            lastScrollY = currentScrollY;

            if (didFirstMeaningfulScroll || didReachScrollRatio) {
                window.removeEventListener('scroll', onScroll);
                document.removeEventListener('scroll', onScroll, true);
                if (timerId) {
                    clearTimeout(timerId);
                    timerId = null;
                }
                showDrawer(didFirstMeaningfulScroll ? 'first_scroll' : 'scroll_ratio');
            }
        };

        timerId = setTimeout(() => {
            window.removeEventListener('scroll', onScroll);
            document.removeEventListener('scroll', onScroll, true);
            showDrawer('timer_20s');
        }, triggerDelayMs);

        window.addEventListener('scroll', onScroll, { passive: true });
        document.addEventListener('scroll', onScroll, true);

        if (debugFlags.forceOpen) {
            showDrawer('debug_force');
        }

        return () => {
            disposed = true;
            if (timerId) clearTimeout(timerId);
            window.removeEventListener('scroll', onScroll);
            document.removeEventListener('scroll', onScroll, true);
        };
    }, [pathname, survey, trackEvent, userData?.id, debugFlags.forceOpen, isMobile, isOpen, isMinimized, isAutoTriggerSuppressed]);

    useEffect(() => {
        if (!ENABLED) return;

        let cancelled = false;

        const loadSurvey = async () => {
            try {
                const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
                if (!baseUrl) return;

                const response = await axios.get(`${baseUrl}/survey/active`, {
                    params: { path: pathname || '' }
                });

                if (cancelled) return;
                if (response?.data?.success && response?.data?.response?.survey) {
                    const remoteSurvey = normalizeSurvey(response.data.response.survey);
                    const isLoggedIn = Number(userData?.id) > 0;
                    if (remoteSurvey && remoteSurvey.audienceMode === 'logged_in' && !isLoggedIn) {
                        setSurvey(null);
                        return;
                    }
                    if (remoteSurvey && remoteSurvey.audienceMode === 'guest' && isLoggedIn) {
                        setSurvey(null);
                        return;
                    }
                    setSurvey(remoteSurvey);
                } else {
                    setSurvey(null);
                }
            } catch (_) {
                setSurvey(null);
            }
        };

        loadSurvey();

        return () => {
            cancelled = true;
        };
    }, [pathname, userData?.id]);

    if (!ENABLED || !survey || (!isOpen && !isMinimized)) {
        return null;
    }

    const runtimeQuestionOrder = questionOrder.length > 0 ? questionOrder : survey.questions.map((q) => q.id);
    const currentQuestionId = runtimeQuestionOrder[currentQuestionIndex] || runtimeQuestionOrder[0];
    const question = survey.questions.find((q) => q.id === currentQuestionId) || survey.questions[0];
    const selectedValue = answers[question.id] || '';
    const suggestion = suggestions[question.id] || '';
    const isLastQuestion = currentQuestionIndex >= (runtimeQuestionOrder.length - 1);
    const hasMoreQuestions = !isLastQuestion;
    const canOfferMore = hasMoreQuestions && survey.followupMode === 'opt_in';

    const showSuggestion = question.negativeValues?.includes(selectedValue);

    const closeForSession = (reason) => {
        setIsOpen(false);
        setIsMinimized(false);
        setIsThankYouVisible(false);
        markSeenThisSession();
        markSeenForWeek(userData?.id);
        trackEvent('survey_dismissed', {
            source: reason,
            path: pathname || '',
            user_id: userData?.id || 0,
            survey_id: survey.id
        });
    };

    const handlePrimaryAction = async () => {
        if (!selectedValue || isSubmitting) return;

        if (!isLastQuestion && survey.followupMode !== 'opt_in') {
            setCurrentQuestionIndex((prev) => prev + 1);
            return;
        }

        setIsSubmitting(true);
        const payload = {
            survey_id: survey.id,
            survey_key: survey.key,
            path: pathname || '',
            user_id: userData?.id || 0,
            answers: survey.questions
                .map((q) => ({
                    question_id: q.id,
                    value: (answers[q.id] || '').trim(),
                    suggestion: (suggestions[q.id] || '').trim()
                }))
                .filter((item) => item.value !== '')
        };

        try {
            const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
            if (baseUrl) {
                await axios.post(`${baseUrl}/survey/submit`, payload);
            }
        } catch (_) {
            // best-effort submit; UX-t nem blokkoljuk backend hiba miatt
        }

        trackEvent('survey_submitted', {
            survey_id: survey.id,
            answers_count: payload.answers.length,
            path: pathname || '',
            user_id: userData?.id || 0
        });

        setIsSubmitting(false);
        markSeenThisSession();
        markSeenForWeek(userData?.id);
        setIsThankYouVisible(true);
    };

    const handleShowNextQuestion = () => {
        if (!selectedValue || !hasMoreQuestions) return;
        setCurrentQuestionIndex((prev) => prev + 1);
    };

    if (isMinimized) {
        return (
            <div className={classes.minimizedDock}>
                <button
                    type="button"
                    className={classes.minimized}
                    onClick={() => {
                        setIsAutoTriggerSuppressed(false);
                        setIsMinimized(false);
                    }}
                >
                    Visszajelzés
                </button>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <div className={classes.drawer}>
                <div className={classes.header}>
                    <p className={classes.title}>{survey.title || 'Gyors kérdőív'}</p>
                    <div className={classes.actions}>
                        {currentQuestionIndex > 0 && (
                            <button type="button" className={classes.iconBtn} aria-label="Előző kérdés" onClick={() => setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))}>
                                {'<'}
                            </button>
                        )}
                        <button
                            type="button"
                            className={classes.iconBtn}
                            aria-label="Minimalizálás"
                            onClick={() => {
                                setIsAutoTriggerSuppressed(true);
                                setIsMinimized(true);
                            }}
                        >
                            _
                        </button>
                        <button type="button" className={classes.iconBtn} aria-label="Bezárás" onClick={() => closeForSession('closed')}>
                            x
                        </button>
                    </div>
                </div>

                <div className={classes.body}>
                    {!isThankYouVisible && (
                        <>
                            <p className={classes.question}>{question.title}</p>
                            <p className={classes.progress}>Kérdés {currentQuestionIndex + 1}/{runtimeQuestionOrder.length}</p>

                            <div className={classes.options}>
                                {question.options.map((option) => (
                                    <label key={option.value} className={classes.option}>
                                        <input
                                            type="radio"
                                            name={`survey-answer-${question.id}`}
                                            value={option.value}
                                            checked={selectedValue === option.value}
                                            onChange={(e) => setAnswers((prev) => ({ ...prev, [question.id]: e.target.value }))}
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>

                            {showSuggestion && (
                                <textarea
                                    className={classes.textarea}
                                    placeholder="Segítsd munkánkat, min módosítanál?"
                                    value={suggestion}
                                    onChange={(e) => setSuggestions((prev) => ({ ...prev, [question.id]: e.target.value }))}
                                />
                            )}

                            <p className={classes.helper}>Most nem zavarunk többet ebben a sessionben, és legfeljebb heti egyszer jelenik meg.</p>

                            <div className={classes.footer}>
                                <button type="button" className={classes.btn} onClick={() => closeForSession('skip')}>
                                    Most nem
                                </button>
                                {canOfferMore && (
                                    <button
                                        type="button"
                                        className={classes.btn}
                                        onClick={handleShowNextQuestion}
                                        disabled={!selectedValue}
                                    >
                                        Szeretnék még
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`${classes.btn} ${classes.btnPrimary}`}
                                    onClick={handlePrimaryAction}
                                    disabled={!selectedValue || isSubmitting}
                                >
                                    {isSubmitting ? 'Küldés...' : (canOfferMore ? 'Küldés most' : (isLastQuestion ? 'Küldés' : 'Tovább'))}
                                </button>
                            </div>
                        </>
                    )}

                    {isThankYouVisible && (
                        <div className={classes.thankYouBox}>
                            <p className={classes.thankYouTitle}>Köszönjük a válaszod!</p>
                            <p className={classes.thankYouText}>Nagy segítség a fejlesztéshez, hogy megosztottad velünk a véleményed.</p>
                            <button type="button" className={`${classes.btn} ${classes.btnPrimary}`} onClick={() => closeForSession('thanks_closed')}>
                                Rendben
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}